import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ExpandedState } from "@tanstack/react-table";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useAccounts, useAccountsByEntity } from "@hooks/useAccounts";
import { useIsClassesEnabled } from "@hooks/useIsClassesEnabled";
import { useEntitiesById } from "@hooks/useEntities";
import { useAccountTransactionsQuery } from "@api/accountingReports.api";
import { JournalEntrySourceType } from "@common/schemas/domains/accounting/journalEntry";
import {
    AccountingReportType,
    ACCOUNTING_REPORT_LABELS,
} from "@common/types/reports/report-type";
import { useGlobalTransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/useGlobalTransactionViewDrawer";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { LoaderOnInitialLoading } from "@design-system/LoaderOnInitialLoading/LoaderOnInitialLoading";
import { Drawer } from "@design-system/Drawer/Drawer";
import { useDrawerContentMount } from "@design-system/Drawer/useDrawerContentMount";
import {
    AccountingTabContext,
    AccountingFiltersSetter,
} from "@components/accounting/useAccountingTab";
import { AccountingReportFilters } from "@components/accounting/types";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CollapseIcon, ExpandIcon } from "@icons";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { LedgerBasisSelect } from "@components/ledger/LedgerBasisSelect";
import { DownloadDropdown } from "@components/accounting/balanceSheet/DownloadDropdown";
import { glDirectionRadios } from "../../pageHeader/filters/Tabs/AmountFilter/directionRadios";
import { AccountingReportFiltersForm } from "../AccountingReportFiltersForm";
import { resolveClassificationAccountCodes } from "../glDrillDown.lib";
import { GeneralLedgerPageContent } from "./GeneralLedgerPageContent";
import {
    defaultGlTransactionsDrilldown,
    GlTransactionsDrilldownConfig,
} from "./glTransactionsDrilldown";
import { getGLExpandedState, shouldAutoExpandGL } from "./glViewBuilder";
import { createGlDrilldownView } from "./glViewConfig";
import { useGeneralLedgerLedger } from "./useGeneralLedgerLedger";
import { useGeneralLedgerDownload } from "./useGeneralLedgerDownload";
import {
    countGeneralLedgerPostingRows,
    getGeneralLedgerDownloadMenuState,
    GENERAL_LEDGER_PDF_TOO_LARGE_TOOLTIP,
} from "./generalLedgerDownloadMenu";

interface ContentProps {
    initialFilters: AccountingReportFilters;
    entityIds: number[];
    ledgerBasis?: AccountingLedgerBasis;
    pnlBalanceSince?: Date;
    open: boolean;
    transactionsDrilldown: GlTransactionsDrilldownConfig;
    emptyView?: () => React.ReactNode;
}

const GlDrilldownDrawerContent: React.FC<ContentProps> = ({
    initialFilters,
    entityIds,
    ledgerBasis,
    pnlBalanceSince,
    open,
    transactionsDrilldown,
    emptyView,
}) => {
    const entitiesById = useEntitiesById();
    const selectedEntity = entitiesById.get(entityIds[0]);
    const [localFilters, setLocalFilters] =
        useState<AccountingReportFilters>(initialFilters);

    const setFilters: AccountingFiltersSetter = useCallback(
        (updater) => setLocalFilters((current) => updater(current)),
        [],
    );

    const handleLedgerIdResolved = useCallback(
        (ledgerId: string) =>
            setLocalFilters((current) => ({ ...current, ledgerId })),
        [],
    );

    // Seeded from the caller-supplied basis (e.g. the invoice's accrual
    // ledger); becomes the live selection once the user toggles Cash/Accrual.
    const [resolvedLedgerBasis, setResolvedLedgerBasis] = useState<
        AccountingLedgerBasis | undefined
    >(ledgerBasis);

    const { resolvedLedgerId, shouldRenderBasisSelect, basisOptions } =
        useGeneralLedgerLedger({
            selectedEntityIds: entityIds,
            ledgerLookupEntityIds: entityIds,
            ledgerId: localFilters.ledgerId,
            onLedgerIdResolved: handleLedgerIdResolved,
            preferredBasis: resolvedLedgerBasis,
            onBasisResolved: setResolvedLedgerBasis,
        });

    const filters = useMemo<AccountingReportFilters>(
        () => ({ ...localFilters, ledgerId: resolvedLedgerId }),
        [localFilters, resolvedLedgerId],
    );

    const contextValue = useMemo(
        () => ({
            filters,
            setFilters,
            selectedEntity,
            ledgerBasis: resolvedLedgerBasis,
            setLedgerBasis: setResolvedLedgerBasis,
        }),
        [filters, setFilters, selectedEntity, resolvedLedgerBasis],
    );

    const queryParams = useMemo(
        () => ({
            filters,
            entityIds,
            pnlBalanceSince,
        }),
        [filters, entityIds, pnlBalanceSince],
    );

    const { ledger, generationDate } = useAccountTransactionsQuery(
        queryParams,
        { enabled: open },
    );

    const { download, isDownloading } = useGeneralLedgerDownload({
        requestDto: queryParams,
        entityIds,
        entitiesById,
        rowVisibility: ledger.data?.presentation?.rowVisibility,
    });

    const glData = useMemo(
        () => ledger.data?.accountsWithTransactions ?? [],
        [ledger.data],
    );

    const downloadMenuState = useMemo(
        () =>
            getGeneralLedgerDownloadMenuState({
                isFetching: ledger.isFetching,
                isSuccess: ledger.isSuccess,
                postingRowCount: countGeneralLedgerPostingRows(glData),
            }),
        [ledger.isFetching, ledger.isSuccess, glData],
    );

    const expandAllState = useMemo(() => getGLExpandedState(glData), [glData]);
    const autoExpand = useMemo(() => shouldAutoExpandGL(glData), [glData]);
    const [expanded, setExpanded] = useState<ExpandedState>({});

    const totalRows = useMemo(
        () =>
            glData.reduce(
                (sum, { transactions }) =>
                    sum + transactions.transactions.length,
                0,
            ),
        [glData],
    );

    const reportKey = useMemo(
        () =>
            `${glData
                .map(({ account }) => account.code)
                .join("|")}:${totalRows}`,
        [glData, totalRows],
    );

    useEffect(() => {
        if (glData.length === 0) {
            setExpanded({});
            return;
        }
        setExpanded(autoExpand ? expandAllState : {});
    }, [glData, autoExpand, expandAllState]);

    const isCollapsed = Object.keys(expanded).length === 0;

    const handleCollapseButtonClick = useCallback(() => {
        setExpanded((currentExpandedState) =>
            Object.keys(currentExpandedState).length === 0
                ? expandAllState
                : {},
        );
    }, [expandAllState]);

    const hasSourceTransactions = useMemo(
        () =>
            glData.some(({ transactions }) =>
                transactions.transactions.some(
                    (t) => t.source.type === JournalEntrySourceType.Transaction,
                ),
            ),
        [glData],
    );

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_GL_DRILLDOWN_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    const openGlobalTransactionViewDrawer = useGlobalTransactionViewDrawer();
    const isClassesEnabled = useIsClassesEnabled();
    const glDrilldownView = useMemo(
        () => createGlDrilldownView({ isClassesEnabled }),
        [isClassesEnabled],
    );

    // The transactions endpoint filters GL accounts by `coaAccounts` (account
    // UUIDs), not by `accountCodes` (numeric COA codes used for reports). The
    // GL drilldown's own account filter stores `accountCodes`, so it must be
    // resolved against the entity's full account list before being handed to
    // the transaction drawer, otherwise the account constraint is silently
    // dropped and "View transactions" returns every transaction in range.
    // Resolved against the full entity account list (not the report's
    // `glData`, which may omit zero-activity accounts) so a selected code
    // always maps to its account id, never silently falls back to "no filter".
    const { accounts: entityAccounts } = useAccounts({
        entityId: selectedEntity?.id,
    });
    const coaAccountIds = useMemo(() => {
        if (!localFilters.accountCodes?.length) {
            return undefined;
        }
        const codes = new Set(localFilters.accountCodes);
        return entityAccounts
            .filter((account) => codes.has(account.code))
            .map((account) => account.id);
    }, [localFilters.accountCodes, entityAccounts]);

    const handleShowTransactions = useCallback(() => {
        const basis = resolvedLedgerBasis ?? AccountingLedgerBasis.cash;
        openGlobalTransactionViewDrawer(
            {
                entityIds,
                coaAccounts: coaAccountIds,
                start: localFilters.startDate,
                end: localFilters.endDate,
                counterpartiesIds: localFilters.counterpartiesIds,
                withEmptyOrUnverifiedCounterparty:
                    localFilters.withEmptyOrUnverifiedCounterparty,
                hasCounterparty: localFilters.hasCounterparty,
                accountIds: localFilters.accountIds,
                subaccountIds: localFilters.subaccountIds,
                excludeFrom1099: localFilters.excludeFrom1099,
            },
            {
                cacheKey: transactionsDrilldown.getCacheKey(basis),
                baseViewOverride: transactionsDrilldown.createView(
                    resolvedLedgerBasis,
                    { isClassesEnabled },
                ),
                shouldSuggestSimilarTransactionsUpdate: true,
            },
        );
    }, [
        openGlobalTransactionViewDrawer,
        entityIds,
        coaAccountIds,
        localFilters,
        resolvedLedgerBasis,
        isClassesEnabled,
        transactionsDrilldown,
    ]);

    return (
        <AccountingTabContext.Provider value={contextValue}>
            <AccountingReportFiltersForm
                filters={localFilters}
                setFilters={setFilters}
                pinnedPresets={pinnedPresets}
                onTogglePin={onTogglePin}
                additionalButtons={
                    <>
                        <Tooltip
                            key={isCollapsed ? "collapse" : "expand"}
                            content={
                                isCollapsed ? "Expand all" : "Collapse all"
                            }
                        >
                            <Button
                                iconOnly
                                variant="primary"
                                size="md"
                                onClick={handleCollapseButtonClick}
                                aria-label={
                                    isCollapsed ? "Expand all" : "Collapse all"
                                }
                            >
                                {isCollapsed ? (
                                    <ExpandIcon />
                                ) : (
                                    <CollapseIcon />
                                )}
                            </Button>
                        </Tooltip>
                        <DownloadDropdown
                            onDownload={(format) => {
                                if (entityIds.length === 0) {
                                    return;
                                }
                                download({
                                    format,
                                    startDate: localFilters.startDate,
                                    endDate: localFilters.endDate,
                                });
                            }}
                            disabled={downloadMenuState.menuDisabled}
                            loading={isDownloading}
                            pdfDisabled={downloadMenuState.pdfDisabled}
                            pdfDisabledTooltip={
                                GENERAL_LEDGER_PDF_TOO_LARGE_TOOLTIP
                            }
                        />
                    </>
                }
                ledgerSelectOverride={
                    shouldRenderBasisSelect ? (
                        <LedgerBasisSelect
                            options={basisOptions}
                            value={resolvedLedgerId}
                            onChange={handleLedgerIdResolved}
                        />
                    ) : undefined
                }
                hideLedgerSelect={!shouldRenderBasisSelect}
                showAccountFilter
                showJournalEntrySourceFilter
                showAmountFilter
                directionRadios={glDirectionRadios}
                showCounterpartyFilter
                showFinancialAccountFilter
                showExclude1099Filter
                hideSearch
                hideEntityFilter
            />
            <LoaderOnInitialLoading queryResult={ledger} dimWhileFetching>
                {ledger.data && selectedEntity && (
                    <>
                        <GeneralLedgerPageContent
                            key={reportKey}
                            data={glData}
                            filters={filters}
                            chrome={ledger.data.presentation.chrome}
                            numberFormat={ledger.data.presentation.numberFormat}
                            generationDate={generationDate}
                            expanded={expanded}
                            onExpandedChange={setExpanded}
                            view={glDrilldownView}
                            openJournalEntriesInPlace
                            transactionsDrilldown={transactionsDrilldown}
                            emptyView={emptyView}
                            footer={
                                hasSourceTransactions ? (
                                    <CommandIsland sticky alignEnd>
                                        <Button
                                            variant="primary"
                                            size="md"
                                            onClick={handleShowTransactions}
                                        >
                                            View transactions
                                        </Button>
                                    </CommandIsland>
                                ) : undefined
                            }
                        />
                    </>
                )}
            </LoaderOnInitialLoading>
        </AccountingTabContext.Provider>
    );
};

interface Props {
    open: boolean;
    onClose: () => void;
    filters: AccountingReportFilters;
    entityIds: number[];
    ledgerBasis?: AccountingLedgerBasis;
    classifications?: AccountClass[];
    pnlBalanceSince?: Date;
    size?: "fullScreen" | "fullScreenWithNav";
    transactionsDrilldown?: GlTransactionsDrilldownConfig;
    emptyView?: () => React.ReactNode;
}

export const GlDrilldownDrawer: React.FC<Props> = ({
    open,
    onClose,
    filters,
    entityIds,
    ledgerBasis,
    classifications,
    pnlBalanceSince,
    size = "fullScreenWithNav",
    transactionsDrilldown = defaultGlTransactionsDrilldown,
    emptyView,
}) => {
    const { shouldRender, openKey, onAnimationEnd } =
        useDrawerContentMount(open);

    const { accountsByEntityId, isLoading: isLoadingAccounts } =
        useAccountsByEntity(entityIds);
    const hasClassifications = (classifications?.length ?? 0) > 0;
    const initialFilters = useMemo<AccountingReportFilters>(
        () => ({
            ...filters,
            accountCodes: resolveClassificationAccountCodes({
                accountCodes: filters.accountCodes,
                classifications,
                accounts: Object.values(accountsByEntityId).flat(),
            }),
        }),
        [filters, classifications, accountsByEntityId],
    );

    return (
        <Drawer
            open={open}
            onClose={onClose}
            direction="right"
            size={size}
            onAnimationEnd={onAnimationEnd}
            headerAddonLeft={
                ACCOUNTING_REPORT_LABELS[AccountingReportType.GENERAL_LEDGER]
            }
            noPadding
        >
            {shouldRender && !(hasClassifications && isLoadingAccounts) && (
                <GlDrilldownDrawerContent
                    key={openKey}
                    initialFilters={initialFilters}
                    entityIds={entityIds}
                    ledgerBasis={ledgerBasis}
                    pnlBalanceSince={pnlBalanceSince}
                    open={open}
                    transactionsDrilldown={transactionsDrilldown}
                    emptyView={emptyView}
                />
            )}
        </Drawer>
    );
};
